import { useEffect, useMemo, useRef, useState } from 'react';
import { Field, label } from './ui.jsx';
import { t } from '../i18n/index.jsx';

/**
 * Type-to-filter company picker. Replaces a plain <select> for invoice
 * recipients: the destinataire list spans clients AND manufacturers (real
 * commission invoices bill manufacturers directly), and once Italy-fund
 * companies are mixed in with Trade ones, a flat dropdown is unusable —
 * search fixes that without having to guess which companies "belong" to
 * which business line.
 */
export default function CompanySelect({ label: fieldLabel, companies, value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const selected = useMemo(() => companies.find((c) => String(c.id) === String(value)) || null, [companies, value]);

  useEffect(() => {
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => c.name.toLowerCase().includes(q));
  }, [companies, query]);

  return (
    <Field label={fieldLabel} error={error}>
      <div className="company-select" ref={rootRef}>
        <button
          type="button"
          className="company-select-trigger"
          onClick={() => setOpen((v) => !v)}
        >
          {selected ? (
            <span>{selected.name} <span className="muted sm">· {label(selected.type)}{selected.country ? ` · ${selected.country}` : ''}</span></span>
          ) : (
            <span className="muted">{t('Select a company…')}</span>
          )}
          <span className="company-select-caret">▾</span>
        </button>
        {open && (
          <div className="company-select-panel">
            <input
              autoFocus
              type="text"
              placeholder={t('Search companies…')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="company-select-list">
              {filtered.length === 0 ? (
                <div className="muted sm" style={{ padding: '10px 12px' }}>{t('No matches')}</div>
              ) : (
                filtered.map((c) => (
                  <div
                    key={c.id}
                    className={`company-select-opt ${String(c.id) === String(value) ? 'on' : ''}`}
                    onClick={() => { onChange(c.id); setOpen(false); setQuery(''); }}
                  >
                    <span>{c.name}</span>
                    <span className="muted sm">{label(c.type)}{c.country ? ` · ${c.country}` : ''}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </Field>
  );
}
